import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  FaDog,
  FaBaby,
  FaTrophy,
  FaImages,
  FaEdit,
  FaCog,
  FaSignOutAlt,
  FaTachometerAlt,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import { useAuth, signOut } from '../lib/useAuth';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { session, loading } = useAuth();

  // Menu nawigacyjne
  const menuItems = [
    {
      path: '/admin/dashboard',
      icon: FaTachometerAlt,
      label: 'Dashboard',
      description: 'Przegląd systemu'
    },
    {
      path: '/admin/dogs',
      icon: FaDog,
      label: 'Psy',
      description: 'Zarządzanie psami'
    },
    {
      path: '/admin/litters',
      icon: FaBaby,
      label: 'Mioty',
      description: 'Zarządzanie miotami'
    },
    {
      path: '/admin/shows',
      icon: FaTrophy,
      label: 'Wystawy',
      description: 'Zarządzanie wystawami'
    },
    {
      path: '/admin/gallery',
      icon: FaImages,
      label: 'Galeria',
      description: 'Zarządzanie zdjęciami'
    },
    {
      path: '/admin/content',
      icon: FaEdit,
      label: 'Treści',
      description: 'Edycja tekstów'
    },
    {
      path: '/admin/settings',
      icon: FaCog,
      label: 'Ustawienia',
      description: 'Konfiguracja systemu'
    }
  ];

  // Wylogowanie
  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  // Ładowanie sesji
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-accent"></div>
      </div>
    );
  }

  // Przekierowanie jeśli nie zalogowany
  if (!session) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <div className={`bg-brand-bg-side text-white transition-all duration-300 ${
        sidebarOpen ? 'w-64' : 'w-16'
      } flex flex-col`}>
        
        {/* Header */}
        <div className="p-4 border-b border-brand-accent/20">
          <div className="flex items-center justify-between">
            <div className={`transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`}>
              <h1 className="text-xl font-bold text-brand-accent">Etsuko CMS</h1>
              <p className="text-xs text-gray-400">Panel administracyjny</p>
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-brand-accent/20 transition-colors"
            >
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center p-3 rounded-lg transition-colors group ${
                      isActive 
                        ? 'bg-brand-accent text-white' 
                        : 'hover:bg-brand-accent/20 text-gray-300'
                    }`}
                    title={!sidebarOpen ? item.label : ''}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <div className={`ml-3 transition-opacity duration-300 ${
                      sidebarOpen ? 'opacity-100' : 'opacity-0 w-0'
                    }`}>
                      <div className="font-medium">{item.label}</div>
                      <div className="text-xs text-gray-400 group-hover:text-gray-300">
                        {item.description}
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-brand-accent/20">
          <button
            onClick={handleLogout}
            className="flex items-center w-full p-3 rounded-lg hover:bg-red-600/20 text-red-400 hover:text-red-300 transition-colors"
            title={!sidebarOpen ? 'Wyloguj' : ''}
          >
            <FaSignOutAlt className="w-5 h-5 flex-shrink-0" />
            <span className={`ml-3 transition-opacity duration-300 ${
              sidebarOpen ? 'opacity-100' : 'opacity-0 w-0'
            }`}>
              Wyloguj
            </span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {menuItems.find(item => item.path === location.pathname)?.label || 'Admin Panel'}
              </h2>
              <p className="text-gray-600 text-sm">
                {menuItems.find(item => item.path === location.pathname)?.description || 'Zarządzanie treścią'}
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Zalogowany jako: <span className="font-medium text-brand-accent">{session.user?.email}</span>
              </div>
              <div className="w-8 h-8 bg-brand-accent rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">{(session.user?.email || 'A')[0].toUpperCase()}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-6 bg-gray-50 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
